import { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import { getAllOrgs } from '@/services/org/get-all-org';
import { useOrgStore } from '@/stores/org-store';
import { Org, User } from '@/types';

export const ROLE_REFRESH_MIN_INTERVAL_MS = 60_000;

/**
 * The org role is read at sign-in. Re-read it when the tab comes back into
 * view (at most once a minute) so a role change -- promoted to owner, say --
 * applies without a reload. Only the current org's role fields change.
 */
export function useOrgRoleRefresh(
  userId: string | undefined,
  setUser: Dispatch<SetStateAction<User | null>>,
) {
  const lastCheck = useRef(Date.now());

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const refresh = async () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck.current < ROLE_REFRESH_MIN_INTERVAL_MS) return;
      lastCheck.current = Date.now();
      const orgId = useOrgStore.getState().currentOrg?.orgid;
      if (!orgId) return;

      const response = await getAllOrgs();
      if (cancelled || response?.detail || !Array.isArray(response?.data)) {
        return;
      }
      const orgs = response.data as Org[];
      const fresh = orgs.find((org) => org.orgid === orgId);
      const { currentOrg, setCurrentOrg, setOrgs } = useOrgStore.getState();
      if (!fresh || currentOrg?.orgid !== orgId) return;
      if (
        fresh.user_role === currentOrg.user_role &&
        (fresh.is_infra_admin ?? false) === (currentOrg.is_infra_admin ?? false)
      ) {
        return;
      }

      const next: Org = {
        ...currentOrg,
        user_role: fresh.user_role,
        is_infra_admin: fresh.is_infra_admin,
      };
      setOrgs(orgs);
      setCurrentOrg(next);
      localStorage.setItem('currentOrg', JSON.stringify(next));
      setUser((prev) =>
        prev
          ? {
              ...prev,
              isOwner: fresh.user_role === 'owner',
              is_infra_admin: fresh.is_infra_admin ?? false,
            }
          : prev,
      );
    };

    const onReturn = () => {
      void refresh();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [userId, setUser]);
}
